import { Box, Stack } from "@mui/material";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import {
  formatTimeWithTimezone,
  getDate,
  getDateInTimezone,
  shouldDisplayYear,
} from "@/forms-design-system/FormDate/helper";
import { FormTypography } from "@/forms-design-system/FormTypography";

type FormDateSelectItemLabelProps = {
  startDate: Date | number;
  endDate: Date | number;
  isSoldOut?: boolean;
  isClosed?: boolean;
  wrapOnMobile?: boolean;
  timezone?: string | null;
};

export const FormDateSelectItemLabel = ({
  startDate,
  endDate,
  isSoldOut = false,
  isClosed = false,
  wrapOnMobile = false,
  timezone,
}: FormDateSelectItemLabelProps) => {
  const { isoLocale } = useLocaleContext();
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();

  const renderLabel = () => {
    const luxonStartDate = timezone ? getDateInTimezone(startDate, timezone) : getDate(startDate);
    const luxonEndDate = timezone ? getDateInTimezone(endDate, timezone) : getDate(endDate);
    const isMultiDayDate = !luxonStartDate.startOf("day").equals(luxonEndDate.startOf("day"));

    const startTimeDisplay = luxonStartDate.toLocaleString(
      { hour: "numeric", minute: "2-digit" },
      { locale: isoLocale }
    );

    const endTimeDisplay = formatTimeWithTimezone(luxonEndDate, isoLocale);

    if (isMultiDayDate) {
      if (isSmallScreen && wrapOnMobile) {
        return (
          <Stack gap={1} width="100%">
            <FormTypography variant="subtitle2" component="span">
              {luxonStartDate.toLocaleString(
                {
                  year: shouldDisplayYear(luxonStartDate) ? "numeric" : undefined,
                  month: "short",
                  day: "numeric",
                  weekday: "short",
                  hour: "numeric",
                  minute: "2-digit",
                },
                { locale: isoLocale }
              )}
              &nbsp;→&nbsp;
            </FormTypography>
            <FormTypography variant="subtitle2" component="span">
              {luxonEndDate.toLocaleString(
                {
                  year: shouldDisplayYear(luxonStartDate) ? "numeric" : undefined,
                  month: "short",
                  day: "numeric",
                  weekday: "short",
                  hour: "numeric",
                  minute: "2-digit",
                },
                { locale: isoLocale }
              )}
            </FormTypography>
          </Stack>
        );
      }

      return (
        <Trans
          space="common"
          i18nKey="dateSelectItemMultiDay"
          components={{
            sb: <FormTypography variant="subtitle2" component="span" />,
          }}
          values={{
            startDate: luxonStartDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonStartDate) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
                weekday: "short",
              },
              { locale: isoLocale }
            ),
            startTime: startTimeDisplay,
            endDate: luxonEndDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonEndDate) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
                weekday: "short",
              },
              { locale: isoLocale }
            ),
            endTime: endTimeDisplay,
          }}
        />
      );
    }

    return (
      <Trans
        space="common"
        i18nKey="dateSelectItemSingleDay"
        components={{
          sb: <FormTypography variant="subtitle2" component="span" />,
        }}
        values={{
          startDate: luxonStartDate.toLocaleString(
            {
              year: shouldDisplayYear(luxonStartDate) ? "numeric" : undefined,
              month: "short",
              day: "numeric",
              weekday: "short",
            },
            { locale: isoLocale }
          ),
          startTime: startTimeDisplay,
          endTime: endTimeDisplay,
        }}
      />
    );
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} width="100%">
      <Box
        sx={{
          flex: "1 1 0%",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {renderLabel()}
      </Box>
      {isClosed && (
        <FormTypography variant="caption" sx={{ flexShrink: 0 }}>
          {t("ticketing", "closed")}
        </FormTypography>
      )}
      {isSoldOut && !isClosed && (
        <FormTypography variant="caption" sx={{ flexShrink: 0 }}>
          {t("ticketing", "soldOut")}
        </FormTypography>
      )}
    </Stack>
  );
};
